// src/generators/dzial7_stereometria/temat3_maturalne/podtemat3_otwarte_types/MaturaTriangularPrismVolumeAreaOpenTask.js

import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';

import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from '../../../../components/inputs/RootFractionInput';

import StereoTriangularPrismAngleDiagram from '../../../../components/diagrams/StereoTriangularPrismAngleDiagram';
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


/*
  Zadanie maturalne – otwarte (4 pkt)

  Graniastosłup prawidłowy trójkątny.
  4 pola odpowiedzi: a, H, V, P_c (1 pkt za każde pole).

  UWAGA (ważne dla runnerów):
  - UniversalQuizRunner ma "coerceAnswerForCheck", które spłaszcza obiekt do String(ua.a.raw)
    jeśli istnieje klucz "a" z {raw: ...}.
  - To rozwala zadania wielopolowe (a/H/V/Pc), bo do checkAnswer trafia string zamiast obiektu.
  - Workaround w tym tasku: trzymamy userAnswer pod kluczami: side/height/volume/area,
    a w UI nadal pokazujemy a/H/V/Pc.
*/

// =======================
// 1) Losowanie danych wejściowych
// =======================

// Typ A: h_Δ = k√3 => a = 2k (a całkowite)
const BASE_HEIGHT_COEFFS_A = [1, 2, 3, 4]; // k

// Typ B: h_Δ = 3t (całkowite) => a = 2t√3 (a pierwiastkowe)
const BASE_HEIGHT_COEFFS_B = [2, 3, 4, 5]; // t

const ANGLES = [30, 45, 60];

function pickOne(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// =======================
// 2) Prosta arytmetyka na ułamkach + zapis w postaci: const + sqrt*√3
// =======================

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x || 1;
}

class Frac {
  constructor(n, d = 1) {
    if (d === 0) throw new Error('Zero denominator');
    const sign = d < 0 ? -1 : 1;
    const nn = n * sign;
    const dd = Math.abs(d);
    const g = gcd(nn, dd);
    this.n = nn / g;
    this.d = dd / g;
  }
  static zero() {
    return new Frac(0, 1);
  }
  static one() {
    return new Frac(1, 1);
  }
  isZero() {
    return this.n === 0;
  }
  add(other) {
    return new Frac(this.n * other.d + other.n * this.d, this.d * other.d);
  }
  mul(other) {
    return new Frac(this.n * other.n, this.d * other.d);
  }
  div(other) {
    return new Frac(this.n * other.d, this.d * other.n);
  }
  abs() {
    return new Frac(Math.abs(this.n), this.d);
  }
}

function fracToLatex(fr) {
  if (fr.d === 1) return String(fr.n);
  return `\\frac{${fr.n}}{${fr.d}}`;
}

function fracTimesSqrt3ToLatex(fr, { forceParens = false } = {}) {
  if (fr.isZero()) return '0';
  const sign = fr.n < 0 ? '-' : '';
  const abs = fr.abs();

  if (abs.n === 1 && abs.d === 1) {
    const core = `\\sqrt{3}`;
    return forceParens ? `${sign}\\left(${core}\\right)` : `${sign}${core}`;
  }

  if (abs.d === 1) {
    const core = `${abs.n}\\sqrt{3}`;
    return forceParens ? `${sign}\\left(${core}\\right)` : `${sign}${core}`;
  }

  const core = `\\frac{${abs.n}\\sqrt{3}}{${abs.d}}`;
  return forceParens ? `${sign}\\left(${core}\\right)` : `${sign}${core}`;
}

function expr(constFrac, sqrtFrac) {
  return { c: constFrac, s: sqrtFrac };
}

function exprToLatex(e, { preferConstFirst = true } = {}) {
  const c = e.c;
  const s = e.s;

  const cZero = c.isZero();
  const sZero = s.isZero();

  if (cZero && sZero) return '0';
  if (!cZero && sZero) return fracToLatex(c);
  if (cZero && !sZero) return fracTimesSqrt3ToLatex(s);

  const cLatex = fracToLatex(c);
  const sLatex = fracTimesSqrt3ToLatex(s);

  if (preferConstFirst) {
    if (s.n < 0) return `${cLatex}${sLatex}`;
    return `${cLatex}+${sLatex}`;
  }

  if (s.n < 0) return `${sLatex}+${cLatex}`;
  return `${sLatex}+${cLatex}`;
}

function mulSqrtBySqrt(aSqrt, bSqrt) {
  return new Frac(3, 1).mul(aSqrt).mul(bSqrt);
}

function mulExpr(e1, e2) {
  const c1 = e1.c;
  const s1 = e1.s;
  const c2 = e2.c;
  const s2 = e2.s;

  const constPart = c1.mul(c2).add(mulSqrtBySqrt(s1, s2));
  const sqrtPart = c1.mul(s2).add(c2.mul(s1));

  return expr(constPart, sqrtPart);
}

function scaleExpr(e, kInt) {
  const k = new Frac(kInt, 1);
  return expr(e.c.mul(k), e.s.mul(k));
}

// =======================
// 3) Funkcje trygonometryczne
// =======================

function tanValue(angle) {
  if (angle === 30) return expr(Frac.zero(), new Frac(1, 3)); // √3/3
  if (angle === 45) return expr(Frac.one(), Frac.zero()); // 1
  if (angle === 60) return expr(Frac.zero(), Frac.one()); // √3
  return expr(Frac.zero(), Frac.zero());
}

function angleToLatex(angle) {
  return `${angle}^{\\circ}`;
}

// =======================
// 4) Budowa danych zadania (A/B)
// =======================

function buildTaskData({ angle, variantType, coeff }) {
  const isA = variantType === 'A';
  const kOrT = coeff;

  const aExpr = isA
    ? expr(new Frac(2 * kOrT, 1), Frac.zero())
    : expr(Frac.zero(), new Frac(2 * kOrT, 1));

  const aLatex = isA ? String(2 * kOrT) : `${2 * kOrT}\\sqrt{3}`;
  const baseHeightLatex = isA
    ? `${kOrT === 1 ? '' : kOrT}\\sqrt{3}`
    : String(3 * kOrT);

  let pCoeff;
  if (!aExpr.c.isZero()) {
    const c = aExpr.c;
    pCoeff = c.mul(c).div(new Frac(4, 1));
  } else {
    const s = aExpr.s;
    pCoeff = new Frac(3, 1).mul(s.mul(s)).div(new Frac(4, 1));
  }
  const baseAreaExpr = expr(Frac.zero(), pCoeff);
  const baseAreaLatex = exprToLatex(baseAreaExpr);

  const t = tanValue(angle);
  const HExpr = mulExpr(aExpr, t);
  const HLatex = exprToLatex(HExpr);

  const VExpr = mulExpr(baseAreaExpr, HExpr);
  const VLatex = exprToLatex(VExpr);

  const twoP = scaleExpr(baseAreaExpr, 2);
  const aH = mulExpr(aExpr, HExpr);
  const threeaH = scaleExpr(aH, 3);

  const PcExpr = expr(twoP.c.add(threeaH.c), twoP.s.add(threeaH.s));
  const PcLatex = exprToLatex(PcExpr, { preferConstFirst: true });
  const PcAltLatex =
    PcExpr.c.isZero() || PcExpr.s.isZero()
      ? null
      : exprToLatex(PcExpr, { preferConstFirst: false });

  return {
    angle,
    variantType,
    coeff: kOrT,
    baseHeightLatex,
    baseAreaLatex,
    aLatex,
    HLatex,
    VLatex,
    PcLatex,
    PcAltLatex,
  };
}

// =======================
// 5) Parser / checker helpers
// =======================

function expectedLatexToRawForParser(latex) {
  if (!latex) return '';

  let s = String(latex).replace(/\s+/g, '').replace(/\\left|\\right/g, '');

  s = s.replace(/\\sqrt\{([^{}]+)\}/g, '√$1');

  s = s
    .replace(/\\dfrac\{([^{}]+)\}\{([^{}]+)\}/g, '$1/$2')
    .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, '$1/$2');

  s = s.replace(/[{}]/g, '').replace(/\\cdot/g, '*');

  return s;
}

function getParsedField(fieldValue) {
  const normalizeUserRaw = (raw) => {
    if (raw == null) return '';
    let s = String(raw);

    if (s.includes('\\')) {
      s = s.replace(/\s+/g, '').replace(/\\left|\\right/g, '');

      s = s.replace(/\\sqrt\{([^{}]+)\}/g, '√$1');

      s = s
        .replace(/\\dfrac\{([^{}]+)\}\{([^{}]+)\}/g, '$1/$2')
        .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, '$1/$2');

      s = s.replace(/\\cdot/g, '*');
      s = s.replace(/[{}]/g, '');
    }

    s = s.replace(/\s+/g, '');
    return s;
  };

  if (fieldValue == null) return null;

  if (typeof fieldValue === 'object') {
    if (fieldValue.parsed != null && Number.isFinite(fieldValue.parsed)) {
      return fieldValue.parsed;
    }

    const raw =
      typeof fieldValue.raw === 'string' || typeof fieldValue.raw === 'number'
        ? normalizeUserRaw(fieldValue.raw)
        : '';

    if (!raw) return null;

    const v = parseRootFractionInput(raw);
    return Number.isFinite(v) ? v : null;
  }

  if (typeof fieldValue === 'string' || typeof fieldValue === 'number') {
    const raw = normalizeUserRaw(fieldValue);
    const v = parseRootFractionInput(raw);
    return Number.isFinite(v) ? v : null;
  }

  return null;
}

function approxEqual(x, y, eps = 1e-2) {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return Math.abs(x - y) <= eps;
}

// =======================
// 6) generator
// =======================

function createOriginalTask() {
  const computed = buildTaskData({
    angle: 60,
    variantType: 'A',
    coeff: 2,
  });

  return {
    type: 'MaturaTriangularPrismVolumeAreaOpenTask',
    data: {
      maxPoints: 4,
      ...computed,
      diagramMinHeight: 175,
      diagramMarginTop: 6,
      diagramMarginBottom: 8,
    },
    tags: ['matura', 'stereometria', 'graniastosłup', 'otwarte'],
    source: {
      variant: 'original',
      exam: 'MATURA',
      session: '2025_SIE',
      taskNumber: 26,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === 'original') {
    return createOriginalTask();
  }

  const angle = pickOne(ANGLES);

  const variantType = Math.random() < 0.5 ? 'A' : 'B';
  const coeff =
    variantType === 'A'
      ? pickOne(BASE_HEIGHT_COEFFS_A)
      : pickOne(BASE_HEIGHT_COEFFS_B);

  const computed = buildTaskData({ angle, variantType, coeff });

  const scaleCoeff = coeff;
  const typeBias = variantType === 'B' ? -10 : 0;

  const diagramMinHeight =
    scaleCoeff <= 2
      ? angle === 30
        ? 150 + typeBias
        : angle === 45
          ? 165 + typeBias
          : 175 + typeBias
      : scaleCoeff === 3
        ? angle === 30
          ? 160 + typeBias
          : angle === 45
            ? 175 + typeBias
            : 190 + typeBias
        : scaleCoeff === 4
          ? angle === 30
            ? 175 + typeBias
            : angle === 45
              ? 200 + typeBias
              : 225 + typeBias
          : angle === 30
            ? 190 + typeBias
            : angle === 45
              ? 215 + typeBias
              : 245 + typeBias;

  return {
    type: 'MaturaTriangularPrismVolumeAreaOpenTask',
    data: {
      maxPoints: 4,
      ...computed,
      diagramMinHeight,
      diagramMarginTop: 6,
      diagramMarginBottom: 8,
    },
    tags: ['matura', 'stereometria', 'graniastosłup', 'otwarte'],
  };
}

// =======================
// 7) treść
// =======================

export function renderTask({ data }) {
  const {
    angle,
    baseHeightLatex,
    diagramMinHeight,
    diagramMarginTop,
    diagramMarginBottom,
  } = data;

  const angleLatex = `${angle}^{\\circ}`;

  const diagramContainerStyle = {
    marginTop: diagramMarginTop ?? 8,
    marginBottom: diagramMarginBottom ?? 10,
    minHeight: diagramMinHeight ?? 180,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  };

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Zadanie maturalne – otwarte (0–4 pkt).</strong>
      </p>

      <p>
        Dany jest graniastosłup prawidłowy trójkątny{' '}
        <InlineMath math={'ABCDEF'} />. Wysokość podstawy{' '}
        <InlineMath math={'ABC'} /> jest równa{' '}
        <InlineMath math={baseHeightLatex} />. Przekątna{' '}
        <InlineMath math={'AE'} /> ściany bocznej{' '}
        <InlineMath math={'ABED'} /> tworzy z krawędzią{' '}
        <InlineMath math={'AB'} /> kąt o mierze{' '}
        <InlineMath math={angleLatex} /> (zobacz rysunek).
      </p>

      <div style={diagramContainerStyle}>
        <StereoTriangularPrismAngleDiagram
          angleDeg={angle}
          baseHeightLatex={baseHeightLatex}
        />
      </div>

      <p>
        Oblicz objętość i pole powierzchni całkowitej tego graniastosłupa.
        Zapisz obliczenia.
      </p>
    </div>
  );
}

// =======================
// 8) input (UWAGA: userAnswer trzymamy pod kluczami side/height/volume/area)
// =======================

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};

  const setField =
    (key) =>
    (val) =>
      onChange({ ...(ua || {}), [key]: val });

  const inputProps = {
    disabled,
    wide: true,
    allowFractions: true,
    allowRoots: true,
  };

  return (
    <div style={{ display: 'grid', rowGap: 18, maxWidth: 420 }}>
      <OpenTaskScoringHint variant="independent" />
      {/* a */}
      <div style={{ display: 'grid', rowGap: 4 }}>
        <div style={{ fontSize: 16 }}>
          długość krawędzi podstawy: <InlineMath math={'a ='} />
        </div>
        <RootFractionInput
          value={ua.side}
          onChange={setField('side')}
          placeholder="np. 6 albo 4√3"
          ariaLabel="długość krawędzi podstawy a"
          label="a"
          {...inputProps}
        />
      </div>

      {/* H */}
      <div style={{ display: 'grid', rowGap: 4 }}>
        <div style={{ fontSize: 16 }}>
          wysokość graniastosłupa: <InlineMath math={'H ='} />
        </div>
        <RootFractionInput
          value={ua.height}
          onChange={setField('height')}
          placeholder="np. 2√3 albo 4√3/3 albo 6"
          ariaLabel="wysokość graniastosłupa H"
          label="H"
          {...inputProps}
        />
      </div>

      {/* V */}
      <div style={{ display: 'grid', rowGap: 4 }}>
        <div style={{ fontSize: 16 }}>
          objętość graniastosłupa: <InlineMath math={'V ='} />
        </div>
        <RootFractionInput
          value={ua.volume}
          onChange={setField('volume')}
          placeholder="np. 54 albo 54√3"
          ariaLabel="objętość graniastosłupa V"
          label="V"
          {...inputProps}
        />
      </div>

      {/* Pc */}
      <div style={{ display: 'grid', rowGap: 4 }}>
        <div style={{ fontSize: 16 }}>
          pole powierzchni całkowitej: <InlineMath math={'P_c ='} />
        </div>
        <RootFractionInput
          value={ua.area}
          onChange={setField('area')}
          placeholder="np. 108 + 18√3 albo 56√3"
          ariaLabel="pole powierzchni całkowitej Pc"
          label="P_c"
          {...inputProps}
        />
      </div>
    </div>
  );
}

// =======================
// 9) sprawdzanie (1 pkt za każde pole)
// =======================

export function checkAnswer({ data }, userAnswer) {
  const ua = userAnswer || {};
  const { aLatex, HLatex, VLatex, PcLatex, PcAltLatex } = data;

  const expectedA = parseRootFractionInput(expectedLatexToRawForParser(aLatex));
  const expectedH = parseRootFractionInput(expectedLatexToRawForParser(HLatex));
  const expectedV = parseRootFractionInput(expectedLatexToRawForParser(VLatex));
  const expectedPc = parseRootFractionInput(expectedLatexToRawForParser(PcLatex));
  const expectedPcAlt =
    PcAltLatex != null
      ? parseRootFractionInput(expectedLatexToRawForParser(PcAltLatex))
      : null;

  // UWAGA: czytamy z side/height/volume/area
  const givenA = getParsedField(ua.side);
  const givenH = getParsedField(ua.height);
  const givenV = getParsedField(ua.volume);
  const givenPc = getParsedField(ua.area);

  const aOk = Number.isFinite(expectedA) && approxEqual(givenA, expectedA);
  const HOk = Number.isFinite(expectedH) && approxEqual(givenH, expectedH);
  const VOk = Number.isFinite(expectedV) && approxEqual(givenV, expectedV);

  const PcOk =
    (Number.isFinite(expectedPc) && approxEqual(givenPc, expectedPc)) ||
    (expectedPcAlt != null &&
      Number.isFinite(expectedPcAlt) &&
      approxEqual(givenPc, expectedPcAlt));

  const score = (aOk ? 1 : 0) + (HOk ? 1 : 0) + (VOk ? 1 : 0) + (PcOk ? 1 : 0);

  return {
    isCorrect: score === 4,
    score,
    maxScore: 4,
    parts: { a: aOk, H: HOk, V: VOk, Pc: PcOk },
    details: { aOk, HOk, VOk, PcOk },
  };
}

// =======================
// 10) poprawna odpowiedź
// =======================

export function renderAnswer({ data }) {
  const { aLatex, HLatex, VLatex, PcLatex } = data;

  return (
    <div style={{ marginTop: 8, display: 'grid', rowGap: 4 }}>
      <InlineMath math={`a = ${aLatex}`} />
      <InlineMath math={`H = ${HLatex}`} />
      <InlineMath math={`V = ${VLatex}`} />
      <div>
        <InlineMath math={`P_c = ${PcLatex}`} />
      </div>
    </div>
  );
}

// =======================
// 11) renderUserAnswer (czytamy side/height/volume/area)
// =======================

export function renderUserAnswer(userAnswer) {
  const rawOrEmpty = (field) => {
    if (field == null) return '';
    if (typeof field === 'object' && field.raw != null) return String(field.raw);
    if (typeof field === 'string' || typeof field === 'number') return String(field);
    return '';
  };

  const rawA = rawOrEmpty(userAnswer?.side);
  const rawH = rawOrEmpty(userAnswer?.height);
  const rawV = rawOrEmpty(userAnswer?.volume);
  const rawPc = rawOrEmpty(userAnswer?.area);

  const allEmpty = !rawA.trim() && !rawH.trim() && !rawV.trim() && !rawPc.trim();

  if (!userAnswer || allEmpty) {
    return <i>(brak odpowiedzi)</i>;
  }

  const row = (labelLatex, raw) => {
    if (!raw || !raw.trim()) {
      return <InlineMath math={`\\displaystyle ${labelLatex}=\\text{–}`} />;
    }
    return <InlineMath math={`\\displaystyle ${formatLatexPreview(raw, labelLatex)}`} />;
    };

  return (
    <div style={{ marginTop: 8, display: 'grid', rowGap: 4 }}>
      {row('a', rawA)}
      {row('H', rawH)}
      {row('V', rawV)}
      {row('P_c', rawPc)}
    </div>
  );
}

// =======================
// 12) wyjaśnienie – Krok 1–4 + dokładne liczenie a
// =======================

export function renderExplanation({ data }) {
  const {
    angle,
    variantType,
    coeff,
    baseHeightLatex,
    aLatex,
    baseAreaLatex,
    HLatex,
    VLatex,
    PcLatex,
  } = data;

  const tanLatex =
    angle === 30 ? '\\frac{\\sqrt{3}}{3}' :
    angle === 45 ? '1' :
    angle === 60 ? '\\sqrt{3}' :
    '';

  let stepA_1 = `a=\\frac{2h_{\\Delta}}{\\sqrt{3}}`;
  let stepA_2 = '';
  let stepA_3 = '';
  let stepA_4 = '';

  if (variantType === 'A') {
    const k = coeff;
    stepA_2 = `a=\\frac{2\\cdot (${k}\\sqrt{3})}{\\sqrt{3}}`;
    stepA_3 = `a=\\frac{2\\cdot${k}\\sqrt{3}}{\\sqrt{3}}`;
    stepA_4 = `a=2\\cdot ${k}=${2 * k}`;
  } else {
    const t = coeff;
    stepA_2 = `a=\\frac{2\\cdot (${3 * t})}{\\sqrt{3}}=\\frac{${6 * t}}{\\sqrt{3}}`;
    stepA_3 = `a=\\frac{${6 * t}\\sqrt{3}}{3}`;
    stepA_4 = `a=${2 * t}\\sqrt{3}`;
  }

  const stepH_1 = `\\tan\\alpha=\\frac{H}{a}`;
  const stepH_2 = `\\tan ${angleToLatex(angle)}=${tanLatex}`;
  const stepH_3 = `H=a\\cdot\\tan ${angleToLatex(angle)}`;
  const stepH_4 = `H=(${aLatex})\\cdot(${tanLatex})=${HLatex}`;

  const stepP1 = `P_{\\Delta}=\\frac{a^{2}\\sqrt{3}}{4}`;
  const stepP2 = `P_{\\Delta}=\\frac{(${aLatex})^{2}\\sqrt{3}}{4}=${baseAreaLatex}`;

  const stepV1 = `V=P_{\\Delta}\\cdot H`;
  const stepV2 = `V=(${baseAreaLatex})\\cdot (${HLatex})=${VLatex}`;

  const stepPc1 = `P_c=2P_{\\Delta}+P_b`;
  const stepPc2 = `P_b=3aH`;
  const stepPc3 = `P_c=2\\cdot(${baseAreaLatex})+3\\cdot(${aLatex})\\cdot(${HLatex})`;
  const stepPc4 = `P_c=${PcLatex}`;

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Krok 1.</strong> W podstawie jest trójkąt równoboczny. Związek między wysokością i bokiem:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={'h_{\\Delta}=\\frac{a\\sqrt{3}}{2}'} />
      </div>

      <p>
        Podstawiamy <InlineMath math={`h_{\\Delta}=${baseHeightLatex}`} /> i wyznaczamy <InlineMath math={'a'} />:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={stepA_1} />
        <BlockMath math={stepA_2} />
        <BlockMath math={stepA_3} />
        <BlockMath math={stepA_4} />
      </div>

      <p>
        <strong>Krok 2.</strong> W ścianie bocznej <InlineMath math={'ABED'} /> mamy prostokąt.
        Przekątna <InlineMath math={'AE'} /> tworzy z <InlineMath math={'AB'} /> kąt <InlineMath math={`${angle}^{\\circ}`} />, więc:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={stepH_1} />
      </div>

      <p>Najpierw wartość tangensa:</p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={stepH_2} />
      </div>

      <p>Teraz liczymy <InlineMath math={'H'} />:</p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={stepH_3} />
        <BlockMath math={stepH_4} />
      </div>

      <p>
        <strong>Krok 3.</strong> Pole podstawy oraz objętość:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={stepP1} />
        <BlockMath math={stepP2} />
        <BlockMath math={stepV1} />
        <BlockMath math={stepV2} />
      </div>

      <p>
        <strong>Krok 4.</strong> Pole powierzchni całkowitej:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={stepPc1} />
        <BlockMath math={stepPc2} />
        <BlockMath math={stepPc3} />
        <BlockMath math={stepPc4} />
      </div>
    </div>
  );
}
